import { useState, useRef, useEffect } from 'react';
import type { Category } from '../types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Layers } from 'lucide-react';

interface CategoryTabsProps {
  categories: Category[];
  selectedCategoryId: number | null;
  onSelectCategory: (id: number | null) => void;
}

export const CategoryTabs = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
}: CategoryTabsProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCategories, setVisibleCategories] = useState<Category[]>([]);
  const [overflowCategories, setOverflowCategories] = useState<Category[]>([]);

  // Determine if we should use dropdown (more than 6 categories)
  useEffect(() => {
    if (categories.length > 6) {
      setVisibleCategories(categories.slice(0, 5));
      setOverflowCategories(categories.slice(5));
      setShowDropdown(true);
    } else {
      setVisibleCategories(categories);
      setOverflowCategories([]);
      setShowDropdown(false);
    }
  }, [categories]);

  const isSelected = (id: number | null) => selectedCategoryId === id;

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
      <div className="container mx-auto max-w-7xl px-4">
        <div
          ref={containerRef}
          className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-3"
        >
          {/* All Posts Tab */}
          <button
            onClick={() => onSelectCategory(null)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all
              ${
                isSelected(null)
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md hover:shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            <Layers className="h-4 w-4" />
            Tất cả
          </button>

          {/* Category Tabs */}
          {visibleCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              className={`
                px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all
                ${
                  isSelected(category.id)
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md hover:shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {category.name}
            </button>
          ))}

          {/* Overflow Dropdown */}
          {showDropdown && overflowCategories.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full bg-gray-100 border-0 hover:bg-gray-200"
                >
                  Thêm
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {overflowCategories.map((category) => (
                  <DropdownMenuItem
                    key={category.id}
                    onClick={() => onSelectCategory(category.id)}
                    className={`cursor-pointer ${
                      isSelected(category.id)
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : ''
                    }`}
                  >
                    {category.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
